import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { PlatformMatomoInterceptor, PlatformMatomoInterceptorRouteData } from '@platform-frontend/platform';
import { LoadingState } from '@platform-frontend/ui-tools';
import { WINDOW } from '@platform-frontend/util';
import { MatomoECommerceView } from 'ngx-matomo-client';
import { debounceTime, filter, map, Observable, switchMap, take, tap } from 'rxjs';
import { AssetState } from './asset';
import { AppFlowType, FlowState } from './flow';
import { ProductState } from './product';
import { ShoppingCartState } from './shopping-cart';
import { StartupConfigurationState } from './startup';

@Injectable()
export class CheckoutMatomoInterceptor<T extends PlatformMatomoInterceptorRouteData = PlatformMatomoInterceptorRouteData> extends PlatformMatomoInterceptor<T> {
  protected readonly store = inject(Store);
  protected readonly window = inject(WINDOW);

  protected extractRouteData(route: ActivatedRouteSnapshot): T {
    return super.extractRouteData(route);
  }

  protected processRouteData(routeData: T): Observable<void> {
    super.processRouteData(routeData);
    return this.store.select(LoadingState.isLoading).pipe(
      debounceTime(1),
      filter(loading => !loading),
      take(1),
      switchMap(() => this.store.selectOnce(FlowState.flowType)),
      tap(flowType => this.tracker.setEcommerceView(this.getECommerceView(flowType))),
      tap(flowType => this.setCustomDimensions(flowType)),
      map(() => void 0)
    );
  }

  private getECommerceView(flowType: AppFlowType): MatomoECommerceView {
    const pricingInformation = this.store.selectSnapshot(ShoppingCartState.pricingInformation);
    const price =
      this.store.selectSnapshot(FlowState.flowType) === 'COMMERCIAL_CHECKOUT' && pricingInformation !== null && pricingInformation !== 'LOADING'
        ? pricingInformation.total
        : flowType === 'PURCHASE_INFORMATION' || flowType === 'SUBSCRIPTION_MANAGEMENT'
        ? this.store.selectSnapshot(AssetState.pricingInformation)?.total
        : undefined;
    return {
      productSKU: this.store.selectSnapshot(FlowState.productIdentifier)?.productId ?? '',
      productName: this.store.selectSnapshot(ProductState.productName) ?? undefined,
      productCategory: this.store.selectSnapshot(ProductState.productCategory) ?? '',
      price
    };
  }

  private setCustomDimensions(flowType: AppFlowType): void {
    this.tracker.setCustomDimension(1, this.window.document.referrer);
    this.tracker.setCustomDimension(2, this.store.selectSnapshot(StartupConfigurationState.vendorName) ?? '');
    this.tracker.setCustomDimension(3, flowType);
    // Attention: Custom dimension 4 is set in the LocaleSetupService
  }
}
